import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const emptyForm = { name: '', email: '', phone: '', company: '', message: '', website: '', consent: false };

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const firstFieldRef = useRef(null);
  const dialogRef = useRef(null);

  // Body scroll lock + focus first field + ESC to close
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => firstFieldRef.current?.focus(), 80);
    const onKey = (e) => e.key === 'Escape' && handleClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setErrors({});
    setSubmitStatus(null);
    onClose();
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Indícanos tu nombre.';
    if (!formData.email.trim()) next.email = 'El email es obligatorio.';
    else if (!EMAIL_RE.test(formData.email.trim())) next.email = 'Introduce un email válido.';
    if (formData.phone && !/^[+\d][\d\s()-]{6,}$/.test(formData.phone.trim()))
      next.phone = 'Introduce un teléfono válido.';
    if (!formData.consent) next.consent = 'Debes aceptar la Política de Privacidad.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setErrorMessage('');
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        ...formData,
        name: formData.name.trim(),
        email: formData.email.trim(),
      });
      setSubmitStatus('success');
      setTimeout(() => {
        handleClose();
        setFormData(emptyForm);
      }, 2200);
    } catch (error) {
      console.error('Error submitting form:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 429 && typeof detail === 'string') {
        setErrorMessage(detail);
      } else {
        setErrorMessage('No hemos podido enviar tu solicitud. Revisa tu conexión e inténtalo de nuevo.');
      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-xl transition-colors duration-200 focus:ring-0 ${
      errors[field]
        ? 'border-red-400 focus:border-red-500'
        : 'border-slate-200 focus:border-blue-500'
    }`;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50"
          />

          {/* Modal wrapper */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <motion.div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="contact-modal-title"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ type: 'spring', duration: 0.45, bounce: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7 sm:p-10 md:p-12"
              data-testid="contact-modal"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-8 gap-4">
                <div>
                  <h2 id="contact-modal-title" className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                    Reserva una consulta gratuita
                  </h2>
                  <p className="text-sm sm:text-base text-slate-600">
                    Cuéntanos qué procesos quieres automatizar y te respondemos en menos de 24&nbsp;horas.
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  aria-label="Cerrar"
                  data-testid="contact-modal-close"
                  className="shrink-0 text-slate-400 hover:text-slate-900 transition-colors p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center text-center py-10"
                  role="status"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-9 h-9 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">¡Solicitud enviada!</h3>
                  <p className="text-slate-600 max-w-sm">
                    Gracias por contactar con Aivanilo. Revisaremos tu mensaje y te responderemos muy pronto.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  {/* Honeypot field — hidden from real users, bots tend to fill it */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                    <label htmlFor="cf-website">No rellenar</label>
                    <input
                      id="cf-website"
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="cf-name" className="block text-sm font-semibold text-slate-700 mb-2">
                        Nombre <span className="text-blue-600">*</span>
                      </label>
                      <Input
                        ref={firstFieldRef}
                        id="cf-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'err-name' : undefined}
                        className={fieldClass('name')}
                        placeholder="Tu nombre"
                        data-testid="cf-name"
                      />
                      {errors.name && (
                        <p id="err-name" className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="block text-sm font-semibold text-slate-700 mb-2">
                        Email <span className="text-blue-600">*</span>
                      </label>
                      <Input
                        id="cf-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'err-email' : undefined}
                        className={fieldClass('email')}
                        placeholder="tu@email.com"
                        data-testid="cf-email"
                      />
                      {errors.email && (
                        <p id="err-email" className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="cf-phone" className="block text-sm font-semibold text-slate-700 mb-2">
                        Teléfono
                      </label>
                      <Input
                        id="cf-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? 'err-phone' : undefined}
                        className={fieldClass('phone')}
                        placeholder="+34 123 456 789"
                        data-testid="cf-phone"
                      />
                      {errors.phone && (
                        <p id="err-phone" className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> {errors.phone}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="cf-company" className="block text-sm font-semibold text-slate-700 mb-2">
                        Empresa
                      </label>
                      <Input
                        id="cf-company"
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={fieldClass('company')}
                        placeholder="Tu empresa"
                        data-testid="cf-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="cf-message" className="block text-sm font-semibold text-slate-700 mb-2">
                      Mensaje
                    </label>
                    <Textarea
                      id="cf-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className={`${fieldClass('message')} resize-none`}
                      placeholder="Cuéntanos qué procesos te gustaría automatizar..."
                      data-testid="cf-message"
                    />
                  </div>

                  {/* Privacy consent — required, not preselected */}
                  <div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="cf-consent"
                        checked={formData.consent}
                        onCheckedChange={(checked) => {
                          setFormData((prev) => ({ ...prev, consent: checked === true }));
                          if (errors.consent) setErrors((prev) => ({ ...prev, consent: undefined }));
                        }}
                        aria-invalid={!!errors.consent}
                        aria-describedby={errors.consent ? 'err-consent' : undefined}
                        data-testid="cf-consent"
                        className="mt-0.5 h-5 w-5 border-2 border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      />
                      <label htmlFor="cf-consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer select-none">
                        He leído y acepto la{' '}
                        <a
                          href="/privacidad"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 font-medium hover:underline"
                          data-testid="cf-privacy-link"
                        >
                          Política de Privacidad
                        </a>
                        .
                      </label>
                    </div>
                    {errors.consent && (
                      <p id="err-consent" className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {errors.consent}
                      </p>
                    )}
                  </div>

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      role="alert"
                      data-testid="contact-error"
                      className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"
                    >
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      {errorMessage || 'No hemos podido enviar tu solicitud. Revisa tu conexión e inténtalo de nuevo.'}
                    </motion.div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="cf-submit"
                    className="w-full bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white py-6 rounded-xl text-lg font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="w-5 h-5 animate-spin" />}
                    {isSubmitting ? 'Enviando…' : 'Enviar solicitud'}
                  </Button>

                  <p className="text-xs text-slate-400 text-center">
                    Usaremos tus datos únicamente para responder a tu solicitud.
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
